import { FilterParamsType } from '@/api/helpers/apiParamsHelpers';
import { formatPayloadDate } from '@/helpers/dateHelpers/dateHelpers';
import { parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { NavigateOptions, useLocation } from 'react-router';

import { useReplaceSearchParams } from '@/hooks/useReplaceSearchParams';
import { useGetSearchParams } from '@/hooks/useGetSearchParams';
import { getString } from '@/bundle/_Opco/_BilledEventsPage/hooks/useBilledEventsFilters/helpers';
import { useUser } from '@/context/userContext';
import { getBillingSubscription } from '@/helpers/billingHelpers';
import { getInitialQueryFilters } from './helpers';

export type BillingEventsFiltersType = {
  transactionName: string;
  dateStart: Date;
  dateEnd: Date;
};

export type BillingEventsFilterParamsType = {
  filters?: string;
  page?: string;
};

export const useBillingEventsFilters = () => {
  const { user } = useUser();
  const { replaceSearchParams, getSearchParams } = useReplaceSearchParams();
  const location = useLocation();
  const queryParams = useGetSearchParams<BillingEventsFilterParamsType>();

  const subscription = getBillingSubscription(user);
  const startDate = subscription.current_period_start;
  const endDate = subscription.current_period_end;
  const filtersParams: FilterParamsType = queryParams?.filters === undefined ? null : JSON.parse(queryParams.filters);
  const queryFilters = getInitialQueryFilters(filtersParams, startDate, endDate);

  const [filters, setFilters] = useState<BillingEventsFiltersType>(null);

  const applyFilters = (newFilters: BillingEventsFiltersType, navigateOptions?: NavigateOptions) => {
    const formattedFiltersParams: FilterParamsType = {
      wire__contract__name: getString(newFilters?.transactionName),
      date_created_after: formatPayloadDate(newFilters?.dateStart),
      date_created_before: formatPayloadDate(newFilters?.dateEnd),
    };
    const searchParams = getSearchParams<BillingEventsFilterParamsType>();
    const stringifiedFiltersParams = JSON.stringify(formattedFiltersParams);

    // Note: skip apply same filters params
    if (searchParams.filters === stringifiedFiltersParams) {
      return;
    }

    const newQueryParams = {
      filters: stringifiedFiltersParams,
      page: null,
    };

    replaceSearchParams(newQueryParams, navigateOptions);
    setFilters(newFilters);
  };

  const clearFilters = () => {
    applyFilters({
      transactionName: '',
      dateStart: parseISO(startDate),
      dateEnd: parseISO(endDate),
    });
  };

  useEffect(() => {
    const initialFilters = {
      dateStart: parseISO(queryFilters.date_created_after),
      dateEnd: parseISO(queryFilters.date_created_before),
      transactionName: queryFilters.wire__contract__name || '',
    };

    if (!queryParams.filters) {
      // Note: use replace on first load, so the browser back button behavior works properly
      applyFilters(initialFilters, { replace: true });
    }

    if (initialFilters) {
      setFilters(initialFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return { filters, queryFilters, applyFilters, clearFilters };
};
